import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import useSEO from '../hooks/useSEO';
import {
  Heart, AlertTriangle, Users, Home as HomeIcon, Pill, HandHeart, UtensilsCrossed,
  Stethoscope, Droplets, Baby, Truck, Search, Building2, Wifi, CheckCircle2,
  MapPin, Shield, Clock, ArrowRight, ChevronLeft, ChevronRight, Zap, Globe, Smartphone, UserPlus
} from 'lucide-react';

const HERO_LOGO = "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png";

/* ── Hero carousel images ── */
const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1774050021082-215a1826d6d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxlbWVyZ2VuY3klMjByZXNjdWUlMjB0ZWFtJTIwaHVtYW5pdGFyaWFuJTIwYWlkfGVufDB8fHx8MTc3NjQwODU3OXww&ixlib=rb-4.1.0&q=85",
    overlay: 'from-[#0B1628] via-[#0B1628]/70 to-transparent',
  },
  {
    url: "https://images.unsplash.com/photo-1773139780754-df8cf8d1ee93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwZW1lcmdlbmN5JTIwZmlyc3QlMjByZXNwb25kZXJ8ZW58MHx8fHwxNzc2NDA4NTc5fDA&ixlib=rb-4.1.0&q=85",
    overlay: 'from-[#0B1628] via-[#0B1628]/70 to-transparent',
  },
  {
    url: "https://images.unsplash.com/photo-1763656445798-7ed72eda7aaf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxlbWVyZ2VuY3klMjByZXNjdWUlMjB0ZWFtJTIwaHVtYW5pdGFyaWFuJTIwYWlkfGVufDB8fHx8MTc3NjQwODU3OXww&ixlib=rb-4.1.0&q=85",
    overlay: 'from-[#0B1628] via-[#0B1628]/70 to-transparent',
  },
  {
    url: "https://images.unsplash.com/photo-1727698947486-cb7689643460?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxkaXNwbGFjZWQlMjBwZW9wbGUlMjBzaGVsdGVyJTIwcmVmdWdlZXN8ZW58MHx8fHwxNzc2NDA4NTc5fDA&ixlib=rb-4.1.0&q=85",
    overlay: 'from-[#0B1628] via-[#0B1628]/70 to-transparent',
  },
];

/* ── App rendering carousel ── */
const APP_RENDERS = [
  {
    url: "https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/bj1j4hmt_ChatGPT%20Image%2017%20apr%202026%2C%2009_15_33.png",
    titleKey: 'render_overview', descKey: 'render_overview_desc',
  },
  { url: "/renders/home.jpg", titleKey: 'render_home', descKey: 'render_home_desc' },
  { url: "/renders/dashboard.jpg", titleKey: 'render_dashboard', descKey: 'render_dashboard_desc' },
  { url: "/renders/map.jpg", titleKey: 'render_map', descKey: 'render_map_desc' },
];

/* ── Emergency scenarios carousel ── */
const SCENARIO_SLIDES = [
  {
    url: "https://images.unsplash.com/photo-1774050021082-215a1826d6d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxlbWVyZ2VuY3klMjByZXNjdWUlMjB0ZWFtJTIwaHVtYW5pdGFyaWFuJTIwYWlkfGVufDB8fHx8MTc3NjQwODU3OXww&ixlib=rb-4.1.0&q=85",
    titleKey: 'slide_rescue', descKey: 'slide_rescue_desc',
  },
  {
    url: "https://images.unsplash.com/photo-1773139780754-df8cf8d1ee93?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwZW1lcmdlbmN5JTIwZmlyc3QlMjByZXNwb25kZXJ8ZW58MHx8fHwxNzc2NDA4NTc5fDA&ixlib=rb-4.1.0&q=85",
    titleKey: 'slide_medical', descKey: 'slide_medical_desc',
  },
  {
    url: "https://images.unsplash.com/photo-1727698947486-cb7689643460?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwyfHxkaXNwbGFjZWQlMjBwZW9wbGUlMjBzaGVsdGVyJTIwcmVmdWdlZXN8ZW58MHx8fHwxNzc2NDA4NTc5fDA&ixlib=rb-4.1.0&q=85",
    titleKey: 'slide_shelter', descKey: 'slide_shelter_desc',
  },
  {
    url: "https://images.unsplash.com/photo-1764684994219-8347a5ab0e5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxlbWVyZ2VuY3klMjByZXNjdWUlMjB0ZWFtJTIwaHVtYW5pdGFyaWFuJTIwYWlkfGVufDB8fHx8MTc3NjQwODU3OXww&ixlib=rb-4.1.0&q=85",
    titleKey: 'slide_disaster', descKey: 'slide_disaster_desc',
  },
];

const TEXTS = {
  it: {
    tagline: 'AIUTO. INTERVENTO. SPERANZA.',
    subtitle: 'La piattaforma che connette chi ha bisogno con chi può aiutare, in tempo reale.',
    cta: 'Registrati', login_cta: 'Accedi',
    features_title: 'Come Funziona',
    feat1_title: 'Segnala l\'Emergenza', feat1_desc: 'Seleziona la categoria, descrivi la situazione e invia la tua posizione GPS con un tocco.',
    feat2_title: 'Geolocalizzazione Automatica', feat2_desc: 'Il sistema rileva la tua posizione e la invia insieme alla richiesta per un intervento rapido.',
    feat3_title: 'Dashboard Operativa', feat3_desc: 'Gli operatori monitorano tutte le richieste in tempo reale su una mappa interattiva.',
    feat4_title: 'Funziona Anche Offline', feat4_desc: 'Le richieste vengono salvate localmente e sincronizzate automaticamente quando torni online.',
    renders_title: 'LIFE LINKE siamo vicini a te',
    render_overview: 'Panoramica Completa', render_overview_desc: 'Interfaccia mobile con form emergenza, dashboard operativa e mappa live integrate.',
    render_home: 'Form Emergenza', render_home_desc: '15 categorie di emergenza, priorità, GPS e invio anonimo in un\'unica schermata.',
    render_dashboard: 'Dashboard Operativa', render_dashboard_desc: 'Statistiche in tempo reale, filtri avanzati, lista richieste e mappa con indicatori.',
    render_map: 'Mappa Live', render_map_desc: 'Visualizzazione geolocalizzata di tutte le segnalazioni con dettagli e stato aggiornato.',
    scenarios_title: 'Scenari di Intervento',
    slide_rescue: 'Soccorso Immediato', slide_rescue_desc: 'Squadre di intervento pronte ad agire in pochi minuti dalla tua segnalazione.',
    slide_medical: 'Emergenza Medica', slide_medical_desc: 'Coordinamento rapido con ambulanze e personale medico sul territorio.',
    slide_shelter: 'Rifugi e Sfollati', slide_shelter_desc: 'Localizzazione rifugi disponibili e gestione sfollati in tempo reale.',
    slide_disaster: 'Risposta ai Disastri', slide_disaster_desc: 'Sistema integrato per la gestione di emergenze su larga scala.',
    categories_title: 'Ogni Emergenza Ha Una Risposta',
    stats_title: 'Pronto Per Ogni Scenario',
    stat1: '15 Categorie', stat1_desc: 'di emergenza coperte',
    stat2: '4 Livelli', stat2_desc: 'di priorità per triage rapido',
    stat3: '5 Lingue', stat3_desc: 'per operare ovunque nel mondo',
    stat4: 'Offline Ready', stat4_desc: 'funziona anche senza internet',
    cta_section_title: 'Ogni Secondo Conta',
    cta_section_desc: 'Registrati ora e contribuisci a salvare vite. LIFE LINKE è gratuito per tutti.',
    footer: '2026 LIFE LINKE. Aiuto. Intervento. Speranza.',
  },
  en: {
    tagline: 'AID. INTERVENTION. HOPE.', subtitle: 'The platform that connects those in need with those who can help, in real-time.',
    cta: 'Register', login_cta: 'Login',
    features_title: 'How It Works',
    feat1_title: 'Report Emergency', feat1_desc: 'Select the category, describe the situation and send your GPS position with one tap.',
    feat2_title: 'Auto Geolocation', feat2_desc: 'The system detects your position and sends it for rapid intervention.',
    feat3_title: 'Operations Dashboard', feat3_desc: 'Operators monitor all requests in real-time on an interactive map.',
    feat4_title: 'Works Offline', feat4_desc: 'Requests are saved locally and synced when you\'re back online.',
    renders_title: 'LIFE LINKE we are close to you',
    render_overview: 'Full Overview', render_overview_desc: 'Mobile interface with emergency form, operations dashboard and live map.',
    render_home: 'Emergency Form', render_home_desc: '15 emergency categories, priority, GPS and anonymous submission.',
    render_dashboard: 'Operations Dashboard', render_dashboard_desc: 'Real-time stats, advanced filters, request list and map.',
    render_map: 'Live Map', render_map_desc: 'Geolocated view of all reports with details and updated status.',
    scenarios_title: 'Intervention Scenarios',
    slide_rescue: 'Immediate Rescue', slide_rescue_desc: 'Response teams ready to act within minutes.',
    slide_medical: 'Medical Emergency', slide_medical_desc: 'Quick coordination with ambulances and medical staff.',
    slide_shelter: 'Shelters & Displaced', slide_shelter_desc: 'Locating shelters and managing displaced persons in real-time.',
    slide_disaster: 'Disaster Response', slide_disaster_desc: 'Integrated system for large-scale emergency management.',
    categories_title: 'Every Emergency Has A Response',
    stats_title: 'Ready For Any Scenario',
    stat1: '15 Categories', stat1_desc: 'of emergencies covered', stat2: '4 Levels', stat2_desc: 'of priority for rapid triage',
    stat3: '5 Languages', stat3_desc: 'to operate anywhere', stat4: 'Offline Ready', stat4_desc: 'works without internet',
    cta_section_title: 'Every Second Counts', cta_section_desc: 'Register now and help save lives. LIFE LINKE is free for everyone.',
    footer: '2026 LIFE LINKE. Aid. Intervention. Hope.',
  },
  fr: {
    tagline: 'AIDE. INTERVENTION. ESPOIR.', subtitle: 'La plateforme qui connecte ceux en besoin avec ceux qui peuvent aider.',
    cta: 'S\'inscrire', login_cta: 'Connexion', features_title: 'Comment Ça Marche',
    feat1_title: 'Signalez l\'Urgence', feat1_desc: 'Sélectionnez la catégorie et envoyez votre position GPS.',
    feat2_title: 'Géolocalisation Auto', feat2_desc: 'Le système détecte et envoie votre position.',
    feat3_title: 'Tableau de Bord', feat3_desc: 'Surveillance en temps réel sur carte interactive.',
    feat4_title: 'Hors Ligne', feat4_desc: 'Sauvegarde locale et sync automatique.',
    renders_title: 'LIFE LINKE nous sommes proches de vous',
    render_overview: 'Vue d\'ensemble', render_overview_desc: 'Interface mobile avec formulaire, dashboard et carte.',
    render_home: 'Formulaire Urgence', render_home_desc: '15 catégories, priorité, GPS et envoi anonyme.',
    render_dashboard: 'Tableau de Bord', render_dashboard_desc: 'Stats temps réel, filtres et carte.',
    render_map: 'Carte Live', render_map_desc: 'Vue géolocalisée de toutes les signalements.',
    scenarios_title: 'Scénarios d\'Intervention',
    slide_rescue: 'Secours Immédiat', slide_rescue_desc: 'Équipes prêtes en quelques minutes.',
    slide_medical: 'Urgence Médicale', slide_medical_desc: 'Coordination rapide avec ambulances.',
    slide_shelter: 'Refuges', slide_shelter_desc: 'Localisation des refuges en temps réel.',
    slide_disaster: 'Catastrophes', slide_disaster_desc: 'Gestion intégrée des urgences.',
    categories_title: 'Chaque Urgence A Une Réponse', stats_title: 'Prêt Pour Tout',
    stat1: '15 Catégories', stat1_desc: 'couvertes', stat2: '4 Niveaux', stat2_desc: 'de priorité',
    stat3: '5 Langues', stat3_desc: 'opérer partout', stat4: 'Hors Ligne', stat4_desc: 'sans internet',
    cta_section_title: 'Chaque Seconde Compte', cta_section_desc: 'Inscrivez-vous et sauvez des vies.',
    footer: '2026 LIFE LINKE. Aide. Intervention. Espoir.',
  },
  zh: {
    tagline: '援助。干预。希望。', subtitle: '实时连接需要帮助的人和能提供帮助的人。',
    cta: '注册', login_cta: '登录', features_title: '工作原理',
    feat1_title: '报告紧急情况', feat1_desc: '选择类别，描述情况，一键发送GPS位置。',
    feat2_title: '自动定位', feat2_desc: '系统检测并发送您的位置。',
    feat3_title: '运营控制台', feat3_desc: '在交互式地图上实时监控所有请求。',
    feat4_title: '离线可用', feat4_desc: '本地保存，上线后自动同步。',
    renders_title: 'LIFE LINKE 我们在你身边',
    render_overview: '完整概览', render_overview_desc: '集成紧急表单、运营面板和实时地图。',
    render_home: '紧急表单', render_home_desc: '15个类别、优先级、GPS和匿名提交。',
    render_dashboard: '运营控制台', render_dashboard_desc: '实时统计、高级筛选和地图。',
    render_map: '实时地图', render_map_desc: '所有报告的地理定位视图。',
    scenarios_title: '干预场景',
    slide_rescue: '紧急救援', slide_rescue_desc: '救援团队几分钟内准备行动。',
    slide_medical: '医疗紧急', slide_medical_desc: '与救护车快速协调。',
    slide_shelter: '避难所', slide_shelter_desc: '实时定位可用避难所。',
    slide_disaster: '灾难响应', slide_disaster_desc: '大规模紧急管理系统。',
    categories_title: '每个紧急情况都有回应', stats_title: '为任何场景做好准备',
    stat1: '15个类别', stat1_desc: '覆盖', stat2: '4个级别', stat2_desc: '快速分诊',
    stat3: '5种语言', stat3_desc: '全球运营', stat4: '离线就绪', stat4_desc: '无网络也能工作',
    cta_section_title: '每一秒都至关重要', cta_section_desc: '立即注册，帮助拯救生命。',
    footer: '2026 LIFE LINKE。援助。干预。希望。',
  },
  ru: {
    tagline: 'ПОМОЩЬ. ВМЕШАТЕЛЬСТВО. НАДЕЖДА.', subtitle: 'Платформа, соединяющая нуждающихся с теми, кто может помочь.',
    cta: 'Регистрация', login_cta: 'Войти', features_title: 'Как Это Работает',
    feat1_title: 'Сообщите о ЧС', feat1_desc: 'Выберите категорию и отправьте GPS-позицию.',
    feat2_title: 'Автогеолокация', feat2_desc: 'Система определяет и отправляет вашу позицию.',
    feat3_title: 'Панель Управления', feat3_desc: 'Мониторинг всех запросов в реальном времени.',
    feat4_title: 'Работает Офлайн', feat4_desc: 'Локальное сохранение и автосинхронизация.',
    renders_title: 'LIFE LINKE мы рядом с вами',
    render_overview: 'Полный Обзор', render_overview_desc: 'Мобильный интерфейс с формой, панелью и картой.',
    render_home: 'Форма ЧС', render_home_desc: '15 категорий, приоритет, GPS и анонимная отправка.',
    render_dashboard: 'Панель Управления', render_dashboard_desc: 'Статистика, фильтры и карта в реальном времени.',
    render_map: 'Карта', render_map_desc: 'Геолоцированный вид всех сообщений.',
    scenarios_title: 'Сценарии Вмешательства',
    slide_rescue: 'Спасение', slide_rescue_desc: 'Группы быстрого реагирования.',
    slide_medical: 'Медицинская ЧС', slide_medical_desc: 'Координация со скорой помощью.',
    slide_shelter: 'Убежища', slide_shelter_desc: 'Поиск убежищ в реальном времени.',
    slide_disaster: 'Катастрофы', slide_disaster_desc: 'Управление крупными ЧС.',
    categories_title: 'На Каждую ЧС Есть Ответ', stats_title: 'Готовы к Любому Сценарию',
    stat1: '15 Категорий', stat1_desc: 'покрыто', stat2: '4 Уровня', stat2_desc: 'приоритета',
    stat3: '5 Языков', stat3_desc: 'для работы в мире', stat4: 'Офлайн', stat4_desc: 'без интернета',
    cta_section_title: 'Каждая Секунда На Счету', cta_section_desc: 'Зарегистрируйтесь и спасайте жизни.',
    footer: '2026 LIFE LINKE. Помощь. Вмешательство. Надежда.',
  },
};

const CATS = [
  { icon: Heart, color: '#FF3B30', key: 'richiesta_aiuto' },
  { icon: AlertTriangle, color: '#FF3B30', key: 'intervento_urgente' },
  { icon: Users, color: '#0A84FF', key: 'sfollati' },
  { icon: HomeIcon, color: '#32D74B', key: 'rifugio' },
  { icon: Pill, color: '#FF9F0A', key: 'farmaci' },
  { icon: HandHeart, color: '#0A84FF', key: 'aiuto_umanitario' },
  { icon: UtensilsCrossed, color: '#FFD60A', key: 'cibo' },
  { icon: Stethoscope, color: '#FF3B30', key: 'emergenza_medica' },
  { icon: Droplets, color: '#0A84FF', key: 'acqua_igiene' },
  { icon: Baby, color: '#FFD60A', key: 'protezione_minori' },
  { icon: Truck, color: '#FF9F0A', key: 'evacuazione_trasporto' },
  { icon: Search, color: '#FF3B30', key: 'persone_scomparse' },
  { icon: Building2, color: '#A0A0A0', key: 'danni_infrastrutturali' },
  { icon: Wifi, color: '#FF9F0A', key: 'blackout_comunicazioni' },
  { icon: CheckCircle2, color: '#32D74B', key: 'checkin_sto_bene' },
];

function useCarousel(items, interval = 5000) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  const next = () => setIdx(i => (i + 1) % items.length);
  const prev = () => setIdx(i => (i - 1 + items.length) % items.length);
  return { idx, next, prev, setIdx };
}

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const lt = (key) => TEXTS[lang]?.[key] || TEXTS['it']?.[key] || key;
  const hero = useCarousel(HERO_IMAGES, 4000);
  const renders = useCarousel(APP_RENDERS, 6000);
  const scenarios = useCarousel(SCENARIO_SLIDES, 5000);

  useSEO({
    title: 'Piattaforma di Emergenza in Tempo Reale',
    description: 'LIFE LINKE collega chi ha bisogno con chi può aiutare. Segnala emergenze mediche, sfollati, rifugio, farmaci con GPS e chatta live con operatori. PWA gratuita, multilingue, offline.',
    keywords: 'emergenza, aiuto, protezione civile, segnalazione emergenza, soccorso, evacuazione, sfollati, rifugio, farmaci, aiuto umanitario, emergenza medica, GPS, chat emergenza, PWA, lifelinke',
  });

  return (
    <div className="min-h-screen bg-[#E8F4FD]" data-testid="landing-page">

      {/* ══════ HERO WITH IMAGE CAROUSEL ══════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background images carousel */}
        {HERO_IMAGES.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{ opacity: hero.idx === i ? 1 : 0 }}
          >
            <img src={img.url} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
        <div className="absolute inset-0 bg-[#0D1B2A]/50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2A]/85 via-[#0D1B2A]/60 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <img src={HERO_LOGO} alt="LIFE LINKE" className="h-24 sm:h-32 w-auto drop-shadow-2xl" data-testid="hero-logo" />
            <div>
              <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-none" style={{textShadow: '0 2px 12px rgba(0,0,0,0.9)'}}>
                <span className="text-white">LIFE</span><span class="text-[#FF3B30]"> LINKE</span>
              </h1>
              <p className="mt-3 text-lg sm:text-xl font-['Barlow_Condensed'] font-bold uppercase tracking-wider text-[#FF3B30]" style={{textShadow: '0 2px 8px rgba(0,0,0,0.8)'}}>
                {lt('tagline')}
              </p>
            </div>
            <p className="text-lg sm:text-xl text-white font-medium leading-relaxed max-w-lg" style={{textShadow: '0 2px 8px rgba(0,0,0,0.8)'}}>
              {lt('subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/registrazione"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#FF1A1A] transition-all shadow-lg shadow-[#FF3B30]/30"
                data-testid="hero-cta-register"
              >
                <UserPlus size={16} />
                {lt('cta')}
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/20 text-white font-bold uppercase tracking-widest text-sm hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 transition-all backdrop-blur-sm"
                data-testid="hero-cta-login"
              >
                {lt('login_cta')}
              </Link>
            </div>
            {/* Hero carousel dots */}
            <div className="flex gap-2 pt-4">
              {HERO_IMAGES.map((_, i) => (
                <button key={i} onClick={() => hero.setIdx(i)} className={`h-1 transition-all duration-300 ${hero.idx === i ? 'bg-[#FF3B30] w-8' : 'bg-white/30 w-4'}`} data-testid={`hero-dot-${i}`} />
              ))}
            </div>
          </div>

          {/* Right: mockup image */}
          <div className="hidden lg:flex justify-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-[#FF3B30]/8 blur-[60px] rounded-full" />
              <img
                src="https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/bj1j4hmt_ChatGPT%20Image%2017%20apr%202026%2C%2009_15_33.png"
                alt="LIFE LINKE App"
                className="relative w-full max-w-[520px] rounded-lg shadow-2xl shadow-black/60"
                data-testid="hero-mockup"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════ FEATURES ══════ */}
      <section className="py-24 bg-[#D6E8F5]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0D1B2A] text-center mb-16">
            {lt('features_title')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, color: '#FF3B30', tKey: 'feat1_title', dKey: 'feat1_desc' },
              { icon: MapPin, color: '#0A84FF', tKey: 'feat2_title', dKey: 'feat2_desc' },
              { icon: Zap, color: '#FFD60A', tKey: 'feat3_title', dKey: 'feat3_desc' },
              { icon: Smartphone, color: '#32D74B', tKey: 'feat4_title', dKey: 'feat4_desc' },
            ].map(({ icon: Icon, color, tKey, dKey }, i) => (
              <div key={i} className="border border-[#B8D4E8] bg-white p-8 hover:border-[#FF3B30]/30 transition-all group" data-testid={`feature-card-${i}`}>
                <div className="w-14 h-14 border border-[#B8D4E8] flex items-center justify-center mb-6 group-hover:border-[#FF3B30]/50 transition-all" style={{ color }}>
                  <Icon size={28} />
                </div>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-tight text-[#0D1B2A] mb-3">{lt(tKey)}</h3>
                <p className="text-sm text-[#4A6B8A] leading-relaxed">{lt(dKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ APP RENDERS CAROUSEL ══════ */}
      <section className="py-24 bg-[#E8F4FD]" data-testid="renders-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0D1B2A] text-center mb-4">
            {lt('renders_title')}
          </h2>
          <div className="w-16 h-1 bg-[#FF3B30] mx-auto mb-12" />

          <div className="relative">
            {/* Main render image */}
            <div className="relative overflow-hidden border border-[#B8D4E8] bg-[#D6E8F5]">
              <div className="relative aspect-[16/9] sm:aspect-[2/1]">
                {APP_RENDERS.map((r, i) => (
                  <img
                    key={i}
                    src={r.url}
                    alt=""
                    className={`absolute inset-0 w-full h-full object-contain bg-[#0A0A0A] transition-opacity duration-700 ${renders.idx === i ? 'opacity-100' : 'opacity-0'}`}
                  />
                ))}
              </div>
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-6 sm:p-8">
                <span className="inline-block px-3 py-1 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest mb-3">
                  {String(renders.idx + 1).padStart(2, '0')} / {String(APP_RENDERS.length).padStart(2, '0')}
                </span>
                <h3 className="font-['Barlow_Condensed'] text-xl sm:text-3xl font-black uppercase tracking-tight text-white mb-1 drop-shadow-lg">
                  {lt(APP_RENDERS[renders.idx].titleKey)}
                </h3>
                <p className="text-sm text-white/80 max-w-lg drop-shadow-md">{lt(APP_RENDERS[renders.idx].descKey)}</p>
              </div>
              {/* Nav arrows */}
              <div className="absolute top-1/2 -translate-y-1/2 left-3 right-3 flex justify-between pointer-events-none">
                <button onClick={renders.prev} className="pointer-events-auto p-3 bg-black/60 border border-white/20 text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] transition-all" data-testid="render-prev">
                  <ChevronLeft size={20} />
                </button>
                <button onClick={renders.next} className="pointer-events-auto p-3 bg-black/60 border border-white/20 text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] transition-all" data-testid="render-next">
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            {/* Thumbnail strip */}
            <div className="flex gap-2 mt-4 justify-center">
              {APP_RENDERS.map((r, i) => (
                <button
                  key={i}
                  onClick={() => renders.setIdx(i)}
                  className={`relative h-16 sm:h-20 w-24 sm:w-32 overflow-hidden border-2 transition-all ${renders.idx === i ? 'border-[#FF3B30] opacity-100' : 'border-[#B8D4E8] opacity-50 hover:opacity-80'}`}
                  data-testid={`render-thumb-${i}`}
                >
                  <img src={r.url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ SCENARIOS CAROUSEL ══════ */}
      <section className="py-24 bg-[#D6E8F5]" data-testid="scenarios-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0D1B2A] text-center mb-12">
            {lt('scenarios_title')}
          </h2>
          <div className="relative overflow-hidden border border-[#B8D4E8]">
            <div className="relative h-[350px] sm:h-[450px]">
              {SCENARIO_SLIDES.map((s, i) => (
                <img key={i} src={s.url} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${scenarios.idx === i ? 'opacity-100' : 'opacity-0'}`} />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
                <span className="inline-block px-3 py-1 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest mb-3">
                  {String(scenarios.idx + 1).padStart(2, '0')} / {String(SCENARIO_SLIDES.length).padStart(2, '0')}
                </span>
                <h3 className="font-['Barlow_Condensed'] text-2xl sm:text-4xl font-black uppercase tracking-tight text-white mb-2 drop-shadow-lg">
                  {lt(SCENARIO_SLIDES[scenarios.idx].titleKey)}
                </h3>
                <p className="text-sm sm:text-base text-white/80 max-w-lg drop-shadow-md">{lt(SCENARIO_SLIDES[scenarios.idx].descKey)}</p>
              </div>
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 left-3 right-3 flex justify-between pointer-events-none">
              <button onClick={scenarios.prev} className="pointer-events-auto p-3 bg-black/60 border border-white/20 text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] transition-all" data-testid="scenario-prev"><ChevronLeft size={20} /></button>
              <button onClick={scenarios.next} className="pointer-events-auto p-3 bg-black/60 border border-white/20 text-white hover:bg-[#FF3B30] hover:border-[#FF3B30] transition-all" data-testid="scenario-next"><ChevronRight size={20} /></button>
            </div>
            <div className="absolute bottom-4 right-6 flex gap-2">
              {SCENARIO_SLIDES.map((_, i) => (
                <button key={i} onClick={() => scenarios.setIdx(i)} className={`h-1.5 transition-all ${scenarios.idx === i ? 'bg-[#FF3B30] w-6' : 'bg-[#0D1B2A]/15 w-3'}`} data-testid={`scenario-dot-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════ CATEGORIES ══════ */}
      <section className="py-24 bg-[#E8F4FD]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0D1B2A] text-center mb-4">{lt('categories_title')}</h2>
          <div className="w-16 h-1 bg-[#FF3B30] mx-auto mb-16" />
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-4">
            {CATS.map(({ icon: Icon, color, key }) => (
              <div key={key} className="flex flex-col items-center gap-3 p-4 border border-[#B8D4E8] bg-[#D6E8F5]/50 hover:border-[#FF3B30]/30 transition-all">
                <div className="w-12 h-12 flex items-center justify-center" style={{ color }}><Icon size={26} /></div>
                <span className="text-[10px] sm:text-xs text-[#4A6B8A] uppercase tracking-wider font-bold text-center leading-tight">{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ STATS ══════ */}
      <section className="py-24 bg-[#D6E8F5]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-black uppercase tracking-tight text-[#0D1B2A] text-center mb-16">{lt('stats_title')}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { v: lt('stat1'), d: lt('stat1_desc'), icon: Shield, color: '#FF3B30' },
              { v: lt('stat2'), d: lt('stat2_desc'), icon: Clock, color: '#FF9F0A' },
              { v: lt('stat3'), d: lt('stat3_desc'), icon: Globe, color: '#0A84FF' },
              { v: lt('stat4'), d: lt('stat4_desc'), icon: Smartphone, color: '#32D74B' },
            ].map(({ v, d, icon: Icon, color }, i) => (
              <div key={i} className="border border-[#B8D4E8] bg-[#E8F4FD] p-8 text-center">
                <Icon size={28} className="mx-auto mb-4" style={{ color }} />
                <div className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase text-[#0D1B2A] mb-2">{v}</div>
                <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════ FINAL CTA ══════ */}
      <section className="py-24 bg-[#E8F4FD] relative overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, #FF3B30 0%, transparent 70%)'}} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center mx-auto mb-8">
            <Heart size={36} className="text-white" />
          </div>
          <h2 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0D1B2A] mb-6">{lt('cta_section_title')}</h2>
          <p className="text-base sm:text-lg text-[#4A6B8A] mb-10 max-w-lg mx-auto">{lt('cta_section_desc')}</p>
          <Link to="/registrazione" className="inline-flex items-center gap-3 px-10 py-5 bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#FF1A1A] transition-all shadow-lg shadow-[#FF3B30]/30" data-testid="final-cta-register">
            <UserPlus size={18} />
            {lt('cta')}
          </Link>
        </div>
      </section>

      {/* ══════ FOOTER ══════ */}
      <footer className="py-8 bg-[#C4D9ED] border-t border-[#B8D4E8]">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={HERO_LOGO} alt="LIFE LINKE" className="h-8 w-auto" />
            <span className="font-['Barlow_Condensed'] font-black text-sm uppercase tracking-tight">
              <span className="text-[#0D1B2A]">LIFE</span><span class="text-[#FF3B30]"> LINKE</span>
            </span>
          </div>
          <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">&copy; {new Date().getFullYear()} ninfa.eu - Tutti i diritti riservati</p>
        </div>
      </footer>
    </div>
  );
}
